import clsx from "clsx";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearch } from "wouter";
import { SyncBadge } from "../../web/components/SyncBadge";
import { ListWarning, Notice, PageHeader, Panel } from "../../web/components/ui";
import { rowOpen } from "../../web/lib/rowOpen";
import { ListToolbar, matchesSearch, NeedsAttention } from "./ListToolbar";
import type { PriceListData } from "./meta";
import { can } from "./data";
import { pricesDueBySupplier, supplierProblems, supplierUrgent } from "./attention";

type Filter = "active" | "attention" | "recheck" | "changed" | "appOnly" | "archived" | "all";
/** The Show list (ListToolbar), as on the other lists. */
const SHOW_LABELS: Record<Filter, string> = { active: "Active suppliers", attention: "Anything needing attention", recheck: "Prices to recheck", changed: "Changed in ServiceM8", appOnly: "Only in this app", archived: "Archived", all: "All" };
const SHOW_GROUPS: { label: string | null; filters: Filter[] }[] = [
    { label: null, filters: ["active", "archived", "all"] },
    { label: "Needs attention", filters: ["attention", "recheck", "changed"] },
    { label: "Kinds of supplier", filters: ["appOnly"] },
];
const FILTERS = Object.keys(SHOW_LABELS) as Filter[];
/** Without See costs there are no supplier prices to show (the server leaves them out), so no rechecks either. */
const NO_COST_GROUPS = SHOW_GROUPS.map((g) => ({ ...g, filters: g.filters.filter((f) => f !== "recheck") }));

/** The suppliers list. Clicking one opens it. */
export function SuppliersList(props: Readonly<{ data: PriceListData; openUuid?: string; onOpen: (uuid: string) => void; onNew: () => void }>) {
    const { data } = props;
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    // Links (e.g. from Home) can open it on a filter: ?filter=recheck.
    const linked = new URLSearchParams(useSearch()).get("filter");
    const seesCosts = can(data, "costs.view");
    const groups = seesCosts ? SHOW_GROUPS : NO_COST_GROUPS;
    const [filter, setFilter] = useState<Filter>(() => (groups.some((g) => g.filters.includes(linked as Filter)) ? (linked as Filter) : "active"));
    const priceCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const p of data.supplierPrices) counts.set(p.supplierUuid, (counts.get(p.supplierUuid) ?? 0) + 1);
        return counts;
    }, [data.supplierPrices]);
    // Changed in ServiceM8, not seen yet (history.ts).
    const changed = useMemo(() => new Set(data.changedInServiceM8.supplier), [data.changedInServiceM8.supplier]);
    // How many of each supplier's prices (for active items) are due for a recheck, and out of date.
    const due = useMemo(
        () => pricesDueBySupplier(data.supplierPrices, new Set(data.materials.filter((m) => m.active).map((m) => m.uuid)), data.settings),
        [data.materials, data.supplierPrices, data.settings],
    );
    /** What needs attention on a supplier (attention.ts), for its mark and the "attention" filter. */
    const problemsOf = (s: (typeof data.suppliers)[number]) => supplierProblems(s.active, due.get(s.uuid), changed.has(s.uuid));
    /** Red, not amber: prices out of date. */
    const urgent = (s: (typeof data.suppliers)[number]) => supplierUrgent(s.active, due.get(s.uuid));
    /** Whether a supplier belongs in the list for this filter (search aside). */
    const inFilter = (f: Filter, s: (typeof data.suppliers)[number]) => {
        if (f === "active") return s.active;
        if (f === "attention") return problemsOf(s).length > 0;
        if (f === "recheck") return s.active && due.has(s.uuid);
        if (f === "changed") return changed.has(s.uuid);
        if (f === "archived") return !s.active;
        if (f === "appOnly") return !s.synced;
        return true;
    };
    const counts = Object.fromEntries(FILTERS.map((f) => [f, data.suppliers.filter((s) => inFilter(f, s)).length])) as Record<Filter, number>;
    const rows = data.suppliers.filter((s) => inFilter(filter, s) && matchesSearch(`${s.name} ${s.email} ${s.phone} ${s.account_number}`, query));

    return (
        <div className="page is-wide">
            <PageHeader
                title="Suppliers"
                subtitle="Who you buy from: their details, your account with them, and their prices."
                actions={
                    can(data, "suppliers.edit") ? (
                        <button type="button" className="btn-primary" onClick={props.onNew}>
                            <Plus className="size-4" /> New supplier
                        </button>
                    ) : null
                }
            />
            {data.supplierSyncUnavailable ? (
                <div className="mb-4">
                    <Notice>{data.supplierSyncUnavailable}</Notice>
                </div>
            ) : null}
            <Panel flush>
                <ListToolbar
                    searchLabel="Search suppliers"
                    placeholder="Search by name, email, phone or account number"
                    query={query}
                    onQuery={setQuery}
                    filter={filter}
                    onFilter={setFilter}
                    groups={groups}
                    labels={SHOW_LABELS}
                    counts={counts}
                />
                <table className="list is-fixed min-w-[720px]">
                    <colgroup>
                        <col className="w-[34%]" />
                        <col className="w-[30%]" />
                        <col className="w-[18%]" />
                        {seesCosts ? <col className="w-[18%]" /> : null}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Supplier</th>
                            <th>Contact</th>
                            <th>Your account</th>
                            {seesCosts ? <th className="text-right">Prices</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((s) => (
                            // The name button covers the whole row (after:inset-0): click selects it, double-click opens the supplier.
                            <tr
                                key={s.uuid}
                                className={clsx("relative", problemsOf(s).length > 0 && (urgent(s) ? "is-danger" : "is-warning"), props.openUuid === s.uuid ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")}
                            >
                                <td className="h-14">
                                    <button type="button" className="flex w-full min-w-0 cursor-default items-center gap-1.5 text-left outline-none after:absolute after:inset-0" {...rowOpen(() => props.onOpen(s.uuid))}>
                                        <SyncBadge synced={s.synced} />
                                        <span className={clsx("truncate font-semibold", !s.active && "text-muted line-through")}>{s.name}</span>
                                        <NeedsAttention problems={problemsOf(s)} danger={urgent(s)} />
                                    </button>
                                </td>
                                <td>
                                    <div className="truncate">{s.phone || <span className="text-muted">-</span>}</div>
                                    <div className="h-4 truncate text-xs text-muted">{s.email}</div>
                                </td>
                                <td>
                                    <div className="truncate">{s.account_number || <span className="text-muted">-</span>}</div>
                                    <div className="h-4 truncate text-xs text-muted">{s.details.hasAccount ? s.details.accountTerms || "On account" : ""}</div>
                                </td>
                                {seesCosts ? (
                                    <td className="text-right">
                                        <PriceCount total={priceCounts.get(s.uuid) ?? 0} due={s.active ? (due.get(s.uuid)?.due ?? 0) : 0} />
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">{data.suppliers.length ? "No suppliers match." : "No suppliers yet."}</p> : null}
            </Panel>
        </div>
    );
}

/** How many prices they have, flagged when some are due for a recheck (how many, on hover). */
function PriceCount({ total, due }: Readonly<{ total: number; due: number }>) {
    if (!due) return <>{total.toLocaleString()}</>;
    return (
        <ListWarning title={`${due.toLocaleString()} of their prices ${due === 1 ? "is" : "are"} due for a recheck. Open the supplier to recheck ${due === 1 ? "it" : "them"}.`}>
            {total.toLocaleString()}
        </ListWarning>
    );
}
